import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { getPolConfig } from "./utils/polAggregators";
import {
  loadGroupWaves, fetchLatestElection, MPCard, loadFullMpData,
} from "./utils/metricsClient";
import { waveDateLabel } from "./utils/waveMeta";
import {
  CLIENT_DATASETS, fetchClientDataset, formatClientMetric,
} from "./utils/clientData";

/* ─── Design tokens ──────────────────────────────────────────────────────── */
const INK    = "#0f172a";
const INK2   = "#64748b";
const INK3   = "#94a3b8";
const BORDER = "#e2e8f0";
const SURF   = "#f8fafc";
const MO_BLUE = "#003087";
// House tokens, shared with the other report views (ConstituencyReportView,
// WaspiReportView, BusinessReportView, HolidayTaxReportView).
const STACK_DARK = "#2c2c2a";

/* ─── Party colours ──────────────────────────────────────────────────────── */
const PARTY_COLORS = {
  Conservative: "#0087DC", Labour: "#D50000", "Liberal Democrat": "#FDBB30",
  "Reform UK": "#12B6CF", Reform: "#12B6CF", Green: "#008066",
  SNP: "#FFF95D", "Plaid Cymru": "#3F8428", Other: "#64748b",
};
/**
 * Party colour, tolerant of naming variants.
 *
 * Election and projection data reach this component from different sources, and
 * an exact-match lookup fails silently — a Conservative seat rendered a grey
 * pill because the 2024 result arrived as "Conservatives". Match on a pattern
 * instead, so plurals, abbreviations and full names all land.
 */
const PARTY_PATTERNS = [
  [/conservative|\btor(y|ies)\b/i, "Conservative"],
  [/labour/i,                      "Labour"],
  [/lib(eral)?\s*dem/i,            "Liberal Democrat"],
  [/reform/i,                      "Reform UK"],
  [/green/i,                       "Green"],
  [/scottish\s*national|\bsnp\b/i, "SNP"],
  [/plaid/i,                       "Plaid Cymru"],
];

const partyName = (p) => {
  const raw = String(p ?? "").trim();
  if (!raw) return null;
  return PARTY_PATTERNS.find(([re]) => re.test(raw))?.[1] || raw;
};

const partyCol = (p) => PARTY_COLORS[partyName(p)] || "#64748b";

/* ─── Change tints ───────────────────────────────────────────────────────── */
const POSBG = "#dcfce7";
const NEGBG = "#fee2e2";

/* ─── Tracking display ───────────────────────────────────────────────────────
 * Two waves of data, so "tracking" here means wave-on-wave change rather than
 * a trend line. Every tracked figure carries one of three markers:
 *   DeltaChip — the change since the previous wave
 *   NewPill   — first asked this wave, so there is nothing to compare
 *   WaveTag   — not asked this wave; the figure shown is the earlier wave's
 * ------------------------------------------------------------------------- */

/** Changes smaller than this are shown as "no change" rather than a direction. */
const FLAT_THRESHOLD = 0.5;

/**
 * Master switch for every wave-on-wave marker (delta chips, NEW pills, the
 * previous-wave bar, the change sentences in the summary).
 *
 * Set to false to publish the latest wave as a standalone snapshot. Worth doing
 * if a pair of waves isn't methodologically comparable — between Jun and Aug
 * 2026, for instance, "don't know" roughly doubles on several questions and the
 * access tradeoff reverses, which reads as a measurement change rather than
 * movement in opinion. Turning this off hides the comparison without touching
 * any of the figures.
 *
 * Currently off: the client wants Wave 2 read on its own. Set back to true to
 * restore the wave-on-wave view — no other change needed.
 */
const SHOW_WAVE_ON_WAVE = false;

/**
 * @param tone  "signal"  — up is good (green), down is bad (red). Default.
 *              "inverse" — up is bad: negative shares, distrust.
 *              "muted"   — direction carries no judgement: neutral, don't know.
 */
const DeltaChip = ({ delta, size = 9, tone = "signal" }) => {
  if (delta == null || !Number.isFinite(delta)) return null;
  const flat = Math.abs(delta) < FLAT_THRESHOLD;
  const up   = delta > 0;
  const good = tone === "inverse" ? !up : up;
  const color = (flat || tone === "muted") ? INK2 : good ? "#15803d" : "#b91c1c";
  const bg    = (flat || tone === "muted") ? "#eef2f6" : good ? POSBG : NEGBG;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: "2px",
      bgcolor: bg, color, borderRadius: "3px",
      px: "3px", py: "1px", fontSize: size, fontWeight: 700,
      lineHeight: 1.2, whiteSpace: "nowrap",
    }}>
      {flat ? "–" : up ? "▲" : "▼"}{flat ? "" : Math.abs(delta).toFixed(0)}
    </Box>
  );
};

const NewPill = ({ size = 8 }) => (
  <Box component="span" sx={{
    display: "inline-flex", alignItems: "center",
    bgcolor: "#e6eaf5", color: MO_BLUE, border: `0.5px solid ${MO_BLUE}55`,
    borderRadius: "3px", px: "3px", py: "1px",
    fontSize: size, fontWeight: 700, letterSpacing: "0.04em",
    lineHeight: 1.2, whiteSpace: "nowrap",
  }}>
    NEW
  </Box>
);

const WaveTag = ({ wave, size = 8 }) => {
  if (wave == null) return null;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center",
      bgcolor: "#f1f5f9", color: INK2, borderRadius: "3px",
      px: "3px", py: "1px", fontSize: size, fontWeight: 600,
      lineHeight: 1.2, whiteSpace: "nowrap",
    }}>
      {waveDateLabel(wave)}
    </Box>
  );
};

/**
 * Legend for the change markers — one line, sits under the header. The NEW
 * clause only appears when something on the page actually carries the pill.
 */
const TrackingKey = ({ wave, prevWave, showNew = false }) => {
  if (prevWave == null) return null;
  return (
    <Typography sx={{ fontSize: 8.5, color: INK3, whiteSpace: "nowrap" }}>
      ▲▼ change vs {waveDateLabel(prevWave)} (pp)
      {showNew && ` · NEW = first asked ${waveDateLabel(wave)}`}
    </Typography>
  );
};

/* ─── Polarity maths ─────────────────────────────────────────────────────────
 * Net scores must treat "question not asked in this wave" as unknown, not as
 * zero: summing missing keys would render a real 0% net score for a question
 * that simply isn't there.
 * ------------------------------------------------------------------------- */

const toArr = (x) => (Array.isArray(x) ? x : x != null ? [x] : []);

/**
 * Read one value. null when the wave doesn't carry it.
 *
 * Number(null) is 0, so a plain Number()/isFinite() check reads "not asked" as
 * a real zero — which is how a statement first asked this wave ended up
 * claiming a +65pp jump from an imaginary Wave-4 score of nothing.
 */
const cell = (byKey, key, field) => {
  const v = byKey?.[key]?.[field];
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Sum a key list, or null when the wave carries none of them. */
function sumKeys(byKey, keys, field) {
  const vals = toArr(keys).map((k) => cell(byKey, k, field));
  if (!vals.some((v) => v != null)) return null;
  return vals.reduce((acc, v) => acc + (v || 0), 0);
}

function netFrom(byKey, polarity, field) {
  const pos = sumKeys(byKey, polarity?.pos, field);
  const neg = sumKeys(byKey, polarity?.neg, field);
  if (pos == null && neg == null) return null;
  return (pos || 0) - (neg || 0);
}

/**
 * The full shape of a likert answer at one wave: positive / neutral / negative
 * / don't know. Returns null when the question wasn't asked in that wave.
 *
 * Kept separate from the net score because for these two waves the movement is
 * into the middle rather than into disagreement, and a net score alone hides
 * that entirely.
 */
function sharesFrom(byKey, polarity, field) {
  const pos = sumKeys(byKey, polarity?.pos, field);
  const neg = sumKeys(byKey, polarity?.neg, field);
  const neu = sumKeys(byKey, polarity?.neu, field);
  const dk  = sumKeys(byKey, polarity?.dk,  field);
  if ([pos, neg, neu, dk].every((v) => v == null)) return null;
  return {
    pos: pos || 0, neu: neu || 0, neg: neg || 0, dk: dk || 0,
    middle: (neu || 0) + (dk || 0),
  };
}

/**
 * Net score per sub-variable of a likert_grid, with wave-on-wave change and the
 * underlying answer shape.
 *
 * Each row: `{ key, label, color, value, national, delta, isNew, fromWave,
 * now, prev }`, where `now`/`prev` are the share objects from sharesFrom.
 * `fromWave` is set when the sub-variable wasn't asked in the latest wave and
 * the value shown comes from the previous one.
 */
function buildNetRows({ parentId, rows, prevWave }) {
  const { groupsById } = getPolConfig();
  const grp = groupsById?.[parentId];
  if (!grp || !rows?.length) return [];
  const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));

  return (grp.subVariables || []).map((subId) => {
    const sg = groupsById?.[subId];
    const pol = sg?.polarity;
    const now      = netFrom(byKey, pol, "constituency");
    const prev     = netFrom(byKey, pol, "prevConstituency");
    const natNow   = netFrom(byKey, pol, "national");
    const natPrev  = netFrom(byKey, pol, "prevNational");
    const stale    = now == null && prev != null;
    const shareNow     = sharesFrom(byKey, pol, "constituency");
    const sharePrev    = sharesFrom(byKey, pol, "prevConstituency");
    const shareNat     = sharesFrom(byKey, pol, "national");
    const shareNatPrev = sharesFrom(byKey, pol, "prevNational");
    return {
      key: subId,
      label: grp.subLabels?.[subId] || sg?.label || subId,
      color: grp.subColors?.[subId] || MO_BLUE,
      value:    stale ? prev    : now,
      national: stale ? natPrev : natNow,
      delta: (now != null && prev != null) ? now - prev : null,
      isNew: now != null && prev == null && prevWave != null,
      fromWave: stale ? prevWave : null,
      now:    stale ? sharePrev    : shareNow,
      prev:   stale ? null         : sharePrev,
      // National shares at the same wave as `now`, for the per-answer
      // vs-national figures.
      nowNat: stale ? shareNatPrev : shareNat,
    };
  }).filter((r) => r.value != null);
}

/**
 * Sum the options of a flat likert group whose keys match a predicate.
 * Returns null when the wave carries none of them (never 0 — see `cell`).
 */
function sumMatching(rows, field, match) {
  if (!rows?.length) return null;
  const vals = rows.filter((r) => match(r.key)).map((r) => {
    const v = r?.[field];
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });
  if (!vals.some((v) => v != null)) return null;
  return vals.reduce((a, v) => a + (v || 0), 0);
}

const isPositiveKey = (k) => k.includes("very_positive") || k.includes("fairly_positive");
const isNegativeKey = (k) => k.includes("very_negative") || k.includes("fairly_negative");
const isNeutralKey  = (k) => k.includes("neither");
const isDKKey       = (k) => k.includes("_DK");
// "Aware" = the top three answers: knows a great deal, knows a fair amount, or
// has heard of it without knowing much. Excludes "heard the name, know nothing"
// and below.
const isAwareKey = (k) =>
  k.includes("great_deal") || k.includes("fair_amount") || k.includes("heard_dont_know_much");

/* ─── Tradeoff wording ───────────────────────────────────────────────────────
 * The two access columns were asked with different wording in each wave (see
 * tradeoff_access vs tradeoff_access2 in polAggregators/motability.js). Since
 * the panel shows the latest wave only, it quotes the latest wording — the
 * "easier vs harder to access" framing, not Wave 1's "tighten the rules to
 * reduce misuse". `**…**` marks the phrase to bold.
 * ------------------------------------------------------------------------- */
const TRADEOFF_FULL = {
  tradeoff_access_dont_tighten_access:
    "It is better to make the Scheme **easy for people to access**, even if there is some risk of people accessing it who shouldn't be able to",
  tradeoff_access_tighten_access:
    "It is better to make the Scheme **harder for people to access**, even if some people who should have access aren't able to use it",
};

/* ─── Text that grows to fill its space ──────────────────────────────────────
 * How much prose the summary produces varies a lot by constituency — some
 * sentences drop out when a figure is unremarkable — so a fixed size leaves
 * half the box empty in some seats and only just fits in others. This grows the
 * type to the largest size that still fits the height flexbox has given it.
 * ------------------------------------------------------------------------- */
const FitText = ({ children, min = 9.5, max = 15, lineHeight = 1.55, sx }) => {
  const ref = useRef(null);
  const lastWidth = useRef(0);

  const fit = () => {
    const el = ref.current;
    if (!el || !el.clientHeight) return;
    // Largest font size whose content still fits the box, by bisection: the
    // relationship between size and wrapped height isn't smooth enough to
    // solve directly.
    let lo = min, hi = max, best = min;
    for (let i = 0; i < 7; i++) {
      const mid = (lo + hi) / 2;
      el.style.fontSize = `${mid}px`;
      if (el.scrollHeight <= el.clientHeight + 1) { best = mid; lo = mid; } else { hi = mid; }
    }
    el.style.fontSize = `${best}px`;
  };

  useLayoutEffect(fit);

  // The page-level fit-to-A4 changes width after this effect runs, and without
  // a React render — so re-fit on any width change rather than trusting the
  // measurement taken during the commit.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      if (Math.abs(w - lastWidth.current) < 0.5) return;
      lastWidth.current = w;
      fit();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Typography ref={ref} sx={{ lineHeight, overflow: "hidden", ...sx }}>
      {children}
    </Typography>
  );
};

/* ─── Inline bold renderer — parses **text** markers ────────────────────── */
const BoldText = ({ text, sx }) => {
  const parts = String(text).split(/\*\*(.*?)\*\*/g);
  return (
    <Typography sx={sx}>
      {parts.map((part, i) =>
        i % 2 === 1
          ? <Box key={i} component="span" sx={{ fontWeight: 700 }}>{part}</Box>
          : part
      )}
    </Typography>
  );
};

/* ─── MO Summary callout ─────────────────────────────────────────────────── */
const MoSummaryBox = ({
  constName, viewData, awarenessData, trustNetData, messageNetData,
  clientData, tradeoffAccess, national = false,
}) => {
  const posNow  = sumMatching(viewData, "constituency", isPositiveKey);
  const posNat  = sumMatching(viewData, "national", isPositiveKey);
  const awareNow  = sumMatching(awarenessData, "constituency", isAwareKey);
  const awareNat  = sumMatching(awarenessData, "national", isAwareKey);

  if (posNow == null && awareNow == null) {
    return (
      <Box sx={{ flex: 1.2, minWidth: 0, border: `1px solid ${BORDER}`, borderTop: `3px solid ${MO_BLUE}`, borderRadius: "8px", bgcolor: "#fff", boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)", p: "10px 14px", display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontSize: 10, color: INK3 }}>Loading…</Typography>
      </Box>
    );
  }

  const posDiff   = (posNow != null && posNat != null) ? posNow - posNat : null;
  const awareDiff = (awareNow != null && awareNat != null) ? awareNow - awareNat : null;

  // The opening line places the seat against the country on the two headline
  // measures. Gaps inside ±3pp aren't worth a direction.
  // `comparator` travels with the phrase: "less well known THAN nationally" but
  // "about as well known AS nationally".
  const gapPhrase = (diff, wording) => {
    if (diff == null) return null;
    if (Math.abs(diff) <= 3) return { level: "same", text: wording.same, pp: "", comparator: "as" };
    return {
      level: diff > 0 ? "above" : "below",
      text: diff > 0 ? wording.above : wording.below,
      pp: ` (${diff >= 0 ? "+" : ""}${diff.toFixed(0)}pp)`,
      comparator: "than",
    };
  };
  const awarePhrase = gapPhrase(awareDiff, {
    same: "about as well known", above: "better known", below: "less well known",
  });
  const supportPhrase = gapPhrase(posDiff, {
    same: "about as well supported", above: "more strongly supported", below: "less well supported",
  });
  const bothTypical = awarePhrase?.level === "same" && supportPhrase?.level === "same";

  // Rank by distance from national locally, by absolute score nationally. A row
  // with no national figure (a question this wave didn't ask) sorts last rather
  // than poisoning the comparison with NaN.
  const vsNat    = (r) => (r.national == null ? -Infinity : r.value - r.national);
  const absVsNat = (r) => (r.national == null ? -Infinity : Math.abs(r.value - r.national));
  const diffOrNull = (r) => {
    const d = r == null ? null : vsNat(r);
    return Number.isFinite(d) ? d : null;
  };

  const topTrust = trustNetData?.length
    ? [...trustNetData].sort((a, b) =>
        national ? b.value - a.value : absVsNat(b) - absVsNat(a))[0]
    : null;
  const topTrustDiff = diffOrNull(topTrust);

  // Two different questions, previously conflated into one wrong sentence:
  // which message lands best here (highest net agreement), and which one this
  // seat reacts to most unlike the country (biggest gap to national). The
  // best-performing message is usually not the most distinctive one.
  const bestMessage = messageNetData?.length
    ? [...messageNetData].sort((a, b) => b.value - a.value)[0]
    : null;
  const bestMessageDiff = diffOrNull(bestMessage);

  const standoutMessage = (!national && messageNetData?.length)
    ? [...messageNetData].sort((a, b) => absVsNat(b) - absVsNat(a))[0]
    : null;
  const standoutDiff = diffOrNull(standoutMessage);
  const standoutIsBest = standoutMessage && bestMessage && standoutMessage.key === bestMessage.key;



  const customers = clientData?.available
    ? Number(national ? clientData.national?.customers : clientData.area?.customers)
    : null;

  /* ── Customer base vs the average seat ─────────────────────────────────── */
  // Counts have no national "average" of their own, so the reference is the
  // mean constituency in the client extract.
  const areasCovered = clientData?.coverage?.areas_covered || 0;
  const meanCustomers = (clientData?.available && areasCovered > 0 && clientData.national?.customers != null)
    ? Number(clientData.national.customers) / areasCovered
    : null;
  const custRatio = (!national && customers != null && meanCustomers)
    ? customers / meanCustomers
    : null;
  const custPhrase = (() => {
    if (custRatio == null) return null;
    if (custRatio >= 1.5)  return "far more";
    if (custRatio >= 1.15) return "more";
    if (custRatio > 1.05)  return "slightly more";
    if (custRatio >= 0.95) return null;          // about average — reworded below
    if (custRatio > 0.85)  return "slightly fewer";
    if (custRatio > 0.5)   return "fewer";
    return "far fewer";
  })();

  const clientDiff = (metric) => {
    const here = Number(clientData?.area?.[metric]);
    const gb   = Number(clientData?.national?.[metric]);
    if (national || !Number.isFinite(here) || !Number.isFinite(gb)) return null;
    return here - gb;
  };
  const over65Diff = clientDiff("pct_over_65");

  /* ── Access trade-off vs national ──────────────────────────────────────── */
  // Framed as a comparison, not a raw split: the raw figure is in the trade-off
  // panel, and the wording changed between waves so the level travels badly.
  const accessGap = (() => {
    if (national || !tradeoffAccess?.length) return null;
    const easy = tradeoffAccess.find((r) => r.key === "tradeoff_access_dont_tighten_access");
    const here = Number(easy?.constituency);
    const gb   = Number(easy?.national);
    if (!Number.isFinite(here) || !Number.isFinite(gb)) return null;
    return here - gb;
  })();

  const bold = { fontWeight: 700 };

  // The one card on the page. Chrome matches the house Callout — white fill,
  // 10px radius, a single accent edge — so it reads as the callout it is rather
  // than as one card among six.
  return (
    <Box sx={{
      flex: 1.2, minWidth: 0,
      border: `1px solid ${BORDER}`, borderTop: `3px solid ${MO_BLUE}`,
      borderRadius: "8px", bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      p: "9px 13px",
      display: "flex", flexDirection: "column", gap: "8px",
    }}>
      <Typography sx={{
        fontSize: 8, fontWeight: 800, letterSpacing: "0.12em",
        textTransform: "uppercase", color: MO_BLUE, flexShrink: 0, lineHeight: 1.1,
      }}>
        Motability in {constName || (national ? "Great Britain" : "this constituency")}
      </Typography>

      {/* Stats strip — one line, not a three-column block of 30px figures.
          The customer count came out entirely: it now sits as the headline of
          the customer-base panel directly below, and printing it twice on one
          sheet bought nothing but height. Aware / positive stay, since neither
          appears anywhere else as a single rolled-up figure. */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "16px", flexShrink: 0 }}>
        {[
          { value: awareNow, diff: awareDiff, label: "aware of the Scheme", color: "#0097A7" },
          { value: posNow,   diff: posDiff,   label: "positive about the Scheme", color: "#2E7D32" },
        ].filter((s) => s && s.value != null).map((s) => (
          <Box key={s.label} sx={{ display: "flex", alignItems: "baseline", gap: "5px", minWidth: 0 }}>
            <Typography sx={{ fontSize: 21, fontWeight: 800, color: s.color, lineHeight: 1 }}>
              {s.value.toFixed(0)}%
            </Typography>
            <Typography sx={{ fontSize: 9.5, color: INK2, lineHeight: 1.2 }}>{s.label}</Typography>
            {!national && s.diff != null && (
              <Typography sx={{ fontSize: 9, color: s.diff >= 0 ? "#2E7D32" : "#C62828", fontWeight: 600 }}>
                {s.diff >= 0 ? "+" : ""}{s.diff.toFixed(0)}pp vs nat
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* Prose — sized to fill whatever height is left in the box. Capped well
          under the old 14px: at full width this paragraph was setting larger
          than any panel heading on the sheet. */}
      <FitText min={9} max={11.5} lineHeight={1.5} sx={{ color: INK, flex: 1 }}>
        {!national && custRatio != null && (
          <>{constName || "This constituency"} has{" "}
          {custPhrase
            ? <><Box component="span" sx={bold}>{custPhrase} Motability customers</Box> than the average seat</>
            : <><Box component="span" sx={bold}>about as many Motability customers</Box> as the average seat</>}
          {/* Age only earns a mention when it's actually distinctive. */}
          {over65Diff != null && Math.abs(over65Diff) > 3 && (
            <>, and <Box component="span" sx={bold}>
              {over65Diff > 0 ? "more" : "fewer"} of them are 65+
            </Box> ({over65Diff >= 0 ? "+" : ""}{over65Diff.toFixed(0)}pp)</>
          )}. </>
        )}
        {!national && awarePhrase && supportPhrase && (
          bothTypical ? (
            <>In {constName || "this constituency"}, Motability is{" "}
            <Box component="span" sx={bold}>roughly as well known and as well supported</Box>{" "}
            as it is across the country. </>
          ) : (
            // The first clause carries the comparison ("… than nationally");
            // the second is elliptical, so it reads with either comparator.
            <>In {constName || "this constituency"}, Motability is{" "}
            <Box component="span" sx={bold}>{awarePhrase.text}</Box>{" "}
            {awarePhrase.comparator} nationally{awarePhrase.pp},{" "}
            {awarePhrase.level === supportPhrase.level ? "and" : "but"}{" "}
            <Box component="span" sx={bold}>{supportPhrase.text}</Box>{supportPhrase.pp}. </>
          )
        )}
        {national && awareNow != null && posNow != null && (
          <>Across Great Britain,{" "}
          <Box component="span" sx={bold}>{awareNow.toFixed(0)}%</Box> have heard of the Motability Scheme and{" "}
          <Box component="span" sx={bold}>{posNow.toFixed(0)}%</Box> are positive about it. </>
        )}
        {topTrust && (
          <>Trust is {national ? "strongest" : (topTrustDiff >= 0 ? "furthest above national" : "furthest below national")} on{" "}
          <Box component="span" sx={bold}>{(topTrust.label || "").toLowerCase()}</Box>{" "}
          (net {topTrust.value > 0 ? "+" : ""}{Math.round(topTrust.value)}%
          {!national && topTrustDiff != null && `, ${topTrustDiff >= 0 ? "+" : ""}${topTrustDiff.toFixed(0)}pp vs nat`}). </>
        )}
        {bestMessage && (
          <>The best-performing message {national ? "is" : "here is"}{" "}
          <Box component="span" sx={bold}>"{bestMessage.label}"</Box>{" "}
          (net {bestMessage.value > 0 ? "+" : ""}{Math.round(bestMessage.value)}%
          {standoutIsBest && bestMessageDiff != null
            ? `), which also lands ${Math.abs(bestMessageDiff).toFixed(0)}pp ${bestMessageDiff >= 0 ? "better" : "worse"} here than nationally. `
            : "). "}
          </>
        )}
        {standoutMessage && !standoutIsBest && standoutDiff != null && Math.abs(standoutDiff) >= 2 && (
          <><Box component="span" sx={bold}>"{standoutMessage.label}"</Box>{" "}
          is the message that most divides this seat from the country, performing{" "}
          <Box component="span" sx={bold}>
            {Math.abs(standoutDiff).toFixed(0)}pp {standoutDiff >= 0 ? "better" : "worse"}
          </Box>{" "}
          than nationally. </>
        )}
        {accessGap != null && (
          Math.abs(accessGap) <= 3 ? (
            <>On the access trade-off, this area is{" "}
            <Box component="span" sx={bold}>in line with the country</Box>. </>
          ) : (
            // Named after the option actually put to respondents — keeping the
            // Scheme easy to access, versus making access harder.
            <>On the access trade-off, this area leans further towards{" "}
            <Box component="span" sx={bold}>
              {accessGap > 0 ? "keeping the Scheme easy to access" : "making access harder"}
            </Box>{" "}
            than the country does (by {Math.abs(accessGap).toFixed(0)}pp). </>
          )
        )}
      </FitText>
    </Box>
  );
};

/* ─── SlimPill ───────────────────────────────────────────────────────────── */
const SlimPill = ({ label, party, share, majority }) => {
  if (!party) return null;
  const col = partyCol(party);
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: "5px",
      bgcolor: `${col}20`, border: `0.5px solid ${col}`, borderRadius: "10px",
      px: "10px", py: "5px", flexShrink: 0,
    }}>
      <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: col }} />
      <Box>
        <Typography sx={{ fontSize: 7.5, fontWeight: 600, color: INK3, lineHeight: 1, mb: "1px" }}>{label}</Typography>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: INK, lineHeight: 1, whiteSpace: "nowrap" }}>
          {partyName(party)}
          {share != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · {Number(share).toFixed(1)}%</Box>}
          {majority != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · Maj {Number(majority).toLocaleString()}</Box>}
        </Typography>
      </Box>
    </Box>
  );
};

const VI_LABELS = {
  "pol:vi:conservative:cat": "Conservative", "pol:vi:labour:cat": "Labour",
  "pol:vi:reform:cat": "Reform UK",          "pol:vi:libdem:cat": "Liberal Democrat",
  "pol:vi:green:cat": "Green",               "pol:vi:snp:cat": "SNP",
  "pol:vi:plaid:cat": "Plaid Cymru",         "pol:vi:other:cat": "Other",
};

/* ─── Header ─────────────────────────────────────────────────────────────── */
const MotabilityHeader = ({ constName, areaCode, topVI, wave, prevWave, showNewKey = false, national = false }) => {
  const [election,   setElection]   = useState(null);
  const [winner2019, setWinner2019] = useState(null);

  useEffect(() => {
    if (national || !areaCode) return;
    let cancelled = false;

    fetchLatestElection(areaCode)
      .then((d) => { if (!cancelled) setElection(d); })
      .catch(() => {});

    const CSV_URL = process.env.PUBLIC_URL + "/static/data/Notional2019Winners_Newpcon.csv";
    fetch(CSV_URL).then((r) => r.text()).then((text) => {
      if (cancelled) return;
      const lines = text.trim().split("\n");
      const header = lines[0].replace(/^﻿/, "").split(",");
      for (const line of lines.slice(1)) {
        const cols = line.split(",");
        const row  = Object.fromEntries(header.map((k, i) => [k.trim(), cols[i]?.trim()]));
        if (row.new_pcon === areaCode) {
          const shares = {
            Conservative: Number(row.con19), Labour: Number(row.lab19),
            "Liberal Democrat": Number(row.ld19), Green: Number(row.green19),
            Reform: Number(row.brx19), SNP: Number(row.snp19),
            "Plaid Cymru": Number(row.pc19), Other: Number(row.other19),
          };
          // The CSV spells two winners differently from the keys above
          // ("LibDem", "PlaidCymru"), so `shares[row.winner]` was undefined and
          // the pill printed 0.0% — and the wrong colour — in the 11 seats
          // those two won in 2019. Map the label onto the canonical name once,
          // before it is used for either.
          const party2019 = { LibDem: "Liberal Democrat", PlaidCymru: "Plaid Cymru" }[row.winner] || row.winner;
          const party = party2019 || Object.entries(shares).reduce((a, b) => (b[1] > (shares[a] || 0) ? b[0] : a), "Other");
          setWinner2019({ party, share: (shares[party] || 0) * 100 });
          break;
        }
      }
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [areaCode, national]);

  const accent = election?.party ? partyCol(election.party) : topVI?.label ? partyCol(topVI.label) : MO_BLUE;

  return (
    <Box sx={{ pb: "6px", borderBottom: `1px solid ${BORDER}`, mb: "8px", flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "3px" }}>
        <Box component="img" src={`${process.env.PUBLIC_URL}/logos/motability-operations.svg`} alt="Motability Operations" sx={{ height: 22 }} />
        <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 14, opacity: 0.6 }} />
        <Box component="span" sx={{
          fontSize: 8, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
          color: MO_BLUE, background: "rgba(0,48,135,0.08)", border: "0.5px solid rgba(0,48,135,0.3)",
          px: "7px", py: "2px", borderRadius: "999px",
        }}>
          {national ? "National Report" : "Constituency Report"}
        </Box>
        {wave != null && (
          <Typography sx={{ fontSize: 9, color: INK2 }}>
            Fieldwork {waveDateLabel(wave)}
            {prevWave != null && <Box component="span" sx={{ color: INK3 }}> · tracked vs {waveDateLabel(prevWave)}</Box>}
          </Typography>
        )}
        <Typography sx={{ ml: "auto", fontSize: 9, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "center", columnGap: "10px", mb: "4px" }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {constName || "Constituency Name"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <SlimPill label={national ? "GB VI Projection" : "VI Projection"} party={topVI?.label} share={topVI?.constituency} />
          {!national && <SlimPill label="2024 Result"   party={election?.party}   share={election?.share} majority={election?.majority} />}
          {!national && <SlimPill label="2019 Result"   party={winner2019?.party} share={winner2019?.share} />}
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box sx={{ height: 3, width: 180, borderRadius: 4, background: `linear-gradient(90deg, ${accent}, ${accent}55)` }} />
        <TrackingKey wave={wave} prevWave={prevWave} showNew={showNewKey} />
      </Box>
    </Box>
  );
};

/* ─── Section label ──────────────────────────────────────────────────────────
 * The house section heading, matching `SL` in the other report views: a small
 * accent dot, the title in dark ink rather than the accent colour, and a
 * hairline rule running out to the panel edge. The accent survives as the dot
 * only — colouring the words as well made each panel shout its own colour.
 * ------------------------------------------------------------------------- */
const SL = ({ children, color = MO_BLUE }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "5px", flexShrink: 0 }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{
      fontSize: 9.5, fontWeight: 700, letterSpacing: "0.09em",
      textTransform: "uppercase", color: STACK_DARK, whiteSpace: "nowrap",
    }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "0.5px", bgcolor: BORDER }} />
  </Box>
);

/* ─── Section wrapper ────────────────────────────────────────────────────────
 * A card, but a quiet one: hairline outline, white fill, no per-panel accent
 * bar. The accent lives in the section label's dot instead, so six panels on
 * one sheet read as one system rather than six competing colour blocks.
 * ------------------------------------------------------------------------- */
const SectionBox = ({ title, accent = "#64748b", children, flex, minHeight, sx: extraSx }) => (
  <Box sx={{
    flex: flex ?? 1, minHeight: minHeight ?? 0, minWidth: 0,
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
    bgcolor: "#fff",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    p: "8px 9px",
    display: "flex", flexDirection: "column",
    ...extraSx,
  }}>
    <SL color={accent}>{title}</SL>
    {children}
  </Box>
);

const Loading = () => (
  <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <Typography sx={{ fontSize: 10, color: INK3 }}>Loading…</Typography>
  </Box>
);

/* ─── General view: composition now vs previous wave ─────────────────────── */
const GeneralViewPanel = ({ view, wave, prevWave, hideNational = false }) => {
  if (!view?.length) return <Loading />;

  const seg = (field) => {
    const pos = sumMatching(view, field, isPositiveKey);
    if (pos == null) return null;
    const neu = sumMatching(view, field, isNeutralKey) || 0;
    const dk  = sumMatching(view, field, isDKKey) || 0;
    return {
      pos,
      neu,
      neg: sumMatching(view, field, isNegativeKey) || 0,
      dk,
      middle: neu + dk,
    };
  };

  const now  = seg("constituency");
  const prev = seg("prevConstituency");
  const nat  = seg("national");
  if (!now) return <Loading />;


  // Where the movement went. Over these two waves it is almost entirely into
  // the middle — neutral and don't know — rather than into negativity, which a
  // headline "% positive" alone would misrepresent as souring opinion.
  const SEGMENTS = [
    { field: "pos", label: "Positive", color: "#2E7D32", tone: "signal" },
    { field: "neu", label: "Neutral",  color: "#9CA3AF", tone: "muted", highlight: true },
    { field: "neg", label: "Negative", color: "#C62828", tone: "inverse" },
    { field: "dk",  label: "Not sure", color: "#D8DEE6", tone: "muted", highlight: true },
  ];

  const StackedBar = ({ s, height, dim = false }) => {
    const total = SEGMENTS.reduce((a, { field }) => a + (s[field] || 0), 0) || 1;
    return (
      <Box sx={{ display: "flex", height, borderRadius: "3px", overflow: "hidden", opacity: dim ? 0.5 : 1 }}>
        {SEGMENTS.map(({ field, color }) => (
          (s[field] || 0) > 0 ? (
            <Box key={field} sx={{ width: `${((s[field] || 0) / total) * 100}%`, bgcolor: color }} />
          ) : null
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "7px" }}>
      <Typography sx={{ fontSize: 9.5, color: INK2, fontStyle: "italic", lineHeight: 1.3 }}>
        "Overall, what is your general view of the Motability Scheme?"
      </Typography>

      {/* Headline figure only: the breakdown below carries the change and the
          gap to national for every answer, positive included. */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "7px" }}>
        <Typography sx={{ fontSize: 34, fontWeight: 800, color: "#2E7D32", lineHeight: 1 }}>
          {now.pos.toFixed(0)}%
        </Typography>
        <Typography sx={{ fontSize: 11, color: INK2, lineHeight: 1.2, pb: "3px" }}>positive</Typography>
      </Box>

      {/* Composition now vs previous wave — centred in the space between the
          headline figure and the breakdown. */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "8px", py: "4px" }}>
        <Box>
          <Typography sx={{ fontSize: 10, color: INK2, mb: "3px", fontWeight: 600 }}>
            {waveDateLabel(wave)}
          </Typography>
          <StackedBar s={now} height={26} />
        </Box>
        {prev && (
          <Box>
            <Typography sx={{ fontSize: 10, color: INK3, mb: "3px" }}>
              {waveDateLabel(prevWave)}
            </Typography>
            <StackedBar s={prev} height={19} dim />
          </Box>
        )}
      </Box>

      {/* Breakdown sits under the bar as four columns, in bar order, so each
          answer reads directly beneath the segment it describes rather than in
          a stacked list off to one side. Four equal columns also line the panel
          up with the trust grid, which is built the same way. */}
      <Box sx={{ display: "flex", gap: "4px", mt: "auto" }}>
        {SEGMENTS.map(({ field, label, color, tone, highlight }) => {
          const value = now[field] || 0;
          const segDelta = prev ? value - (prev[field] || 0) : null;
          const natDiff = (!hideNational && nat) ? value - (nat[field] || 0) : null;
          const moved = highlight && segDelta != null && Math.abs(segDelta) >= 2;
          return (
            <Box key={field} sx={{
              flex: 1, minWidth: 0,
              ...(moved && { bgcolor: "#eef2f6", borderRadius: "3px", px: "3px", mx: "-3px" }),
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "3px", minWidth: 0 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
                <Typography sx={{
                  fontSize: 9.5, color: INK2, lineHeight: 1.2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {label}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "3px", mt: "2px", whiteSpace: "nowrap" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK, lineHeight: 1 }}>
                  {value.toFixed(0)}%
                </Typography>
                <DeltaChip delta={segDelta} size={9} tone={tone} />
              </Box>
              {natDiff != null && (
                <Typography sx={{ fontSize: 8.5, color: INK3, lineHeight: 1.2, mt: "1px", whiteSpace: "nowrap" }}>
                  {natDiff >= 0 ? "+" : ""}{natDiff.toFixed(0)}pp vs nat
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ─── Awareness chart ────────────────────────────────────────────────────── */
const AwarenessChart = ({ data, hideNational = false }) => {
  if (!data?.length) return <Loading />;

  const maxVal = Math.max(75, ...data.map((d) => Number(d.constituency) || 0), 1);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Typography sx={{ fontSize: 9.5, color: INK2, mb: "6px", fontStyle: "italic" }}>
        "Have you heard of the Motability Scheme before today?"
      </Typography>
      {/* space-between: the six answers spread to fill the panel instead of
          bunching at the top and leaving a gap under the last one. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "5px", flex: 1, justifyContent: "space-between" }}>
        {data.map((d) => {
          const pct     = Number(d.constituency) || 0;
          const nat     = Number(d.national) || 0;
          const barW    = (pct / maxVal) * 100;
          const barDiff = pct - nat;
          return (
            <Box key={d.key}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: "2px" }}>
                <Typography sx={{ fontSize: 10, color: INK, lineHeight: 1.3, flex: 1, pr: "6px" }}>{d.label}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: d.color || INK }}>{pct.toFixed(0)}%</Typography>
                  <DeltaChip delta={d.delta} />
                  {!hideNational && (
                    <Typography sx={{ fontSize: 9, color: INK3, minWidth: 46, textAlign: "right" }}>
                      {barDiff === 0 ? "= nat" : `${barDiff > 0 ? "+" : ""}${barDiff.toFixed(0)}pp vs nat`}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ position: "relative", height: 12, borderRadius: "4px", bgcolor: BORDER, overflow: "visible" }}>
                <Box sx={{ height: "100%", width: `${barW}%`, bgcolor: d.color || "#64748b", borderRadius: "4px" }} />
                {!hideNational && nat > 0 && (
                  <Box sx={{
                    position: "absolute", top: -2.5, bottom: -2.5,
                    left: `${(nat / maxVal) * 100}%`,
                    width: "2px", bgcolor: INK3, borderRadius: "1px",
                  }} />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      {!hideNational && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mt: "6px" }}>
          <Box sx={{ width: "2px", height: 10, bgcolor: INK3, borderRadius: "1px" }} />
          <Typography sx={{ fontSize: 9.5, color: INK3 }}>National average</Typography>
        </Box>
      )}
    </Box>
  );
};

/* ─── Trust: composition per aspect ──────────────────────────────────────────
 * A diverging net-score bar answers "how much trust" but not "where did it
 * go" — and across these two waves trust doesn't move to distrust, it moves to
 * neither/not-sure. So each aspect gets a full 100% bar with the middle marked,
 * the net score alongside, and one line summarising the average shift.
 * ------------------------------------------------------------------------- */

const TRUST_SEGMENTS = [
  { field: "pos", label: "Trust",    color: "#1565C0", tone: "signal" },
  { field: "neu", label: "Neither",  color: "#9CA3AF", tone: "muted" },
  { field: "neg", label: "Distrust", color: "#C62828", tone: "inverse" },
  { field: "dk",  label: "Not sure", color: "#D8DEE6", tone: "muted" },
];

/**
 * One bar per answer, in the same four columns as the figures above it.
 *
 * A single 100% stacked bar can't line up with its labels — the segments are
 * proportional while the labels need equal columns — so this splits into four
 * tracks sharing one 0–100 scale. Lengths stay comparable both across the four
 * answers and down a column between aspects.
 */
const CompositionBar = ({ shares, height = 12 }) => (
  <Box sx={{ display: "flex", gap: "4px" }}>
    {TRUST_SEGMENTS.map(({ field, color }) => (
      <Box key={field} sx={{ flex: 1, minWidth: 0, height, borderRadius: "3px", bgcolor: BORDER, overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${Math.min(shares?.[field] || 0, 100)}%`, bgcolor: color }} />
      </Box>
    ))}
  </Box>
);

const TrustComposition = ({ rows, caption, hideNational = false }) => {
  if (!rows?.length) return <Loading />;

  const sorted = [...rows].sort((a, b) => b.value - a.value);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      {caption && (
        <Typography sx={{ fontSize: 10, color: INK2, mb: "7px", fontStyle: "italic" }}>
          {caption}
        </Typography>
      )}

      {/* Column headers — named once here so each aspect's row can be just
          numbers rather than repeating "Trust / Neither / …" five times. */}
      <Box sx={{ display: "flex", gap: "4px", mb: "5px", pl: "1px" }}>
        {TRUST_SEGMENTS.map(({ field, label, color }) => (
          <Box key={field} sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "4px" }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 9.5, color: INK3, whiteSpace: "nowrap" }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {sorted.map((row) => (
          <Box key={row.key}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px", mb: "3px" }}>
              <Typography sx={{ fontSize: 10.5, color: INK, lineHeight: 1.25, flex: 1, minWidth: 0 }}>
                {row.label}
              </Typography>
              {row.isNew && <NewPill />}
              {row.fromWave != null && <WaveTag wave={row.fromWave} />}
            </Box>

            {/* Each answer's share, its movement, and its gap to national —
                all in the header's columns. */}
            <Box sx={{ display: "flex", gap: "4px", mb: "3px", pl: "1px" }}>
              {TRUST_SEGMENTS.map(({ field, tone }) => {
                const value = row.now?.[field];
                const segDelta = (row.now && row.prev)
                  ? (row.now[field] || 0) - (row.prev[field] || 0)
                  : null;
                const natDiff = (!hideNational && row.now && row.nowNat)
                  ? (row.now[field] || 0) - (row.nowNat[field] || 0)
                  : null;
                return (
                  <Box key={field} sx={{
                    flex: 1, minWidth: 0,
                    display: "flex", alignItems: "center", gap: "3px", whiteSpace: "nowrap",
                  }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: INK, lineHeight: 1 }}>
                      {value == null ? "—" : `${value.toFixed(0)}%`}
                    </Typography>
                    {natDiff != null && (
                      <Typography sx={{ fontSize: 8.5, color: INK3, lineHeight: 1 }}>
                        {natDiff >= 0 ? "+" : ""}{natDiff.toFixed(0)}pp vs nat
                      </Typography>
                    )}
                    <DeltaChip delta={segDelta} size={8.5} tone={tone} />
                  </Box>
                );
              })}
            </Box>

            <CompositionBar shares={row.now} height={12} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ─── Diverging net-score rows (messages) ────────────────────────────────────
 * Deliberately wave-free: six of the nine statements were first asked this
 * wave, so the panel compares the messages against each other and against
 * national, not against a previous wave. No deltas, no NEW pills.
 * ------------------------------------------------------------------------- */
const NetScoreRows = ({
  // labelWidth runs wide deliberately: a statement that wraps to two lines
  // sets the whole row's height, so buying horizontal room for the label is
  // the cheapest vertical saving on the page.
  rows, caption, leftLabel, rightLabel, labelWidth = 200,
  defaultColor, hideNational = false, barHeight = 15,
}) => {
  if (!rows?.length) return <Loading />;

  const sorted = [...rows].sort((a, b) => b.value - a.value);
  const maxAbs = Math.max(10, ...sorted.map((r) => Math.abs(r.value)),
    ...(hideNational ? [] : sorted.map((r) => Math.abs(r.national ?? 0))));

  const fillPct = (v) => `${Math.max((Math.abs(v) / maxAbs) * 50, 0.3)}%`;
  const dotLeft = (v) => `calc(50% + ${(v / maxAbs) * 50}% - 3px)`;

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      {caption && (
        <Typography sx={{ fontSize: 9.5, color: INK2, mb: "4px", fontStyle: "italic" }}>{caption}</Typography>
      )}
      <Box sx={{ display: "flex", mb: "3px", alignItems: "center", gap: "8px" }}>
        <Box sx={{ width: labelWidth, flexShrink: 0 }} />
        <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between", px: "4px" }}>
          <Typography sx={{ fontSize: 9, color: INK3 }}>← {leftLabel}</Typography>
          <Typography sx={{ fontSize: 9, color: INK3 }}>{rightLabel} →</Typography>
        </Box>
        <Box sx={{ width: 62 }} />
      </Box>
      {sorted.map((row) => {
        const isPos   = row.value >= 0;
        const relDiff = row.national != null ? row.value - row.national : null;
        return (
          <Box key={row.key} sx={{ display: "flex", alignItems: "center", mb: "5px", gap: "8px" }}>
            <Box sx={{ width: labelWidth, flexShrink: 0 }}>
              <Typography sx={{ fontSize: 10, color: INK, lineHeight: 1.25 }}>{row.label}</Typography>
            </Box>
            <Box sx={{ flex: 1, height: barHeight, bgcolor: BORDER, borderRadius: "8px", position: "relative" }}>
              <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1.5px", bgcolor: INK3 }} />
              <Box sx={{
                position: "absolute", top: "2px", height: barHeight - 4, borderRadius: "6px",
                bgcolor: isPos ? (row.color || defaultColor) : "#C62828",
                left: isPos ? "50%" : `calc(50% - ${fillPct(row.value)})`,
                width: fillPct(row.value),
              }} />
              {!hideNational && row.national != null && (
                <Box sx={{
                  position: "absolute", top: "50%", transform: "translateY(-50%)",
                  left: dotLeft(row.national),
                  width: 7, height: 7, borderRadius: "50%",
                  bgcolor: INK3, border: `2px solid ${SURF}`,
                }} />
              )}
            </Box>
            <Box sx={{ width: 62, flexShrink: 0, textAlign: "right" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "3px" }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isPos ? "#2E7D32" : "#C62828", lineHeight: 1 }}>
                  {row.value > 0 ? "+" : ""}{Math.round(row.value)}%
                </Typography>
              </Box>
              {!hideNational && relDiff != null && (
                <Typography sx={{ fontSize: 8.5, color: INK3, lineHeight: 1.3, mt: "1px" }}>
                  {relDiff >= 0 ? "+" : ""}{relDiff.toFixed(0)}pp vs nat
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
      {!hideNational && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "5px", alignItems: "center", mt: "auto" }}>
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: INK3, border: `2px solid ${SURF}` }} />
          <Typography sx={{ fontSize: 9, color: INK3 }}>National average</Typography>
        </Box>
      )}
    </Box>
  );
};

/* ─── Tradeoff stat box ──────────────────────────────────────────────────── */
const TradeoffStat = ({ data, title, accent, hideNational = false }) => {
  const sorted2 = data?.length
    ? [...data].filter((d) => Number.isFinite(Number(d.constituency)))
        .sort((a, b) => Number(b.constituency) - Number(a.constituency))
    : [];
  const first  = sorted2[0];
  const second = sorted2[1];
  if (!first) {
    return (
      <SectionBox title={title} accent={accent} flex={1} minHeight={80}>
        <Loading />
      </SectionBox>
    );
  }
  const line = (row, big) => {
    const pct  = Math.round(Number(row.constituency) || 0);
    const nat  = Math.round(Number(row.national) || 0);
    const diff = pct - nat;
    const label = TRADEOFF_FULL[row.key] || row.label || "";
    return (
      <>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "7px" }}>
          <Typography sx={{ fontSize: big ? 28 : 19, fontWeight: big ? 800 : 700, color: row.color || (big ? "#1565C0" : "#C62828"), lineHeight: 1 }}>
            {pct}%
          </Typography>
          {/* Current wave only — no wave-on-wave chip. */}
          {!hideNational && (
            <Typography sx={{ fontSize: 9, color: INK3 }}>
              {diff === 0 ? "= nat" : `${diff > 0 ? "+" : ""}${diff}pp vs nat`}
            </Typography>
          )}
        </Box>
        <BoldText text={label} sx={{ fontSize: big ? 10.5 : 10, color: big ? INK : INK2, mt: "3px", lineHeight: 1.35 }} />
      </>
    );
  };
  return (
    <SectionBox title={title} accent={accent} flex={1} minHeight={80}>
      {/* Centred as a pair, not spread. This panel sits beside the message
          list and inherits its height, and space-evenly pushed two short
          figures to opposite ends of an otherwise empty column. */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "10px" }}>
        <Box>{line(first, true)}</Box>
        {second && (
          <Box sx={{ borderTop: `1px solid ${BORDER}`, pt: "10px" }}>{line(second, false)}</Box>
        )}
      </Box>
    </SectionBox>
  );
};

/* ─── Client data: Motability customer base ──────────────────────────────── */

/** "2026-08-14T12:25:38+00:00" → "14 Aug 2026". Falls back to the raw date. */
function formatLoadedDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const ClientCustomersPanel = ({ clientData, national = false }) => {
  const spec = CLIENT_DATASETS.motability_customers;
  const row  = national ? clientData?.national : clientData?.area;

  if (!clientData?.available) return null;

  const coverage = clientData.coverage;
  const areasCovered = coverage?.areas_covered || 0;

  if (!row) {
    return (
      <SectionBox title="Motability customer base" accent={MO_BLUE} flex={1} minHeight={170}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Typography sx={{ fontSize: 10, color: INK2, lineHeight: 1.4 }}>
            No customer data supplied for this constituency.
          </Typography>
          <Typography sx={{ fontSize: 9, color: INK3, mt: "3px" }}>
            The extract currently covers {areasCovered} of {coverage?.areas_expected || 632} constituencies.
          </Typography>
        </Box>
      </SectionBox>
    );
  }

  // Counts don't have a national "average" — compare against the mean
  // constituency in the extract so the number has a reference point.
  const gbMeanCustomers = areasCovered > 0 && clientData.national?.customers != null
    ? Number(clientData.national.customers) / areasCovered
    : null;
  const customers = Number(row.customers);
  const vsMean = (!national && gbMeanCustomers) ? customers - gbMeanCustomers : null;

  const smallStats = ["pct_under_16", "pct_over_65", "avg_imd"];
  const bands = spec.bands.filter((b) => Number.isFinite(Number(row[b])));
  const bandTotal = bands.reduce((a, b) => a + Number(row[b]), 0) || 1;

  return (
    <SectionBox title="Motability customer base" accent={MO_BLUE} flex={1} minHeight={170}>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "7px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
          <Typography sx={{ fontSize: 30, fontWeight: 800, color: MO_BLUE, lineHeight: 1 }}>
            {formatClientMetric(customers, "count")}
          </Typography>
          <Box sx={{ pb: "1px" }}>
            <Typography sx={{ fontSize: 11.5, color: INK2, lineHeight: 1.2 }}>
              Scheme customers{national ? " in GB" : ""}
            </Typography>
            {vsMean != null && (
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: vsMean >= 0 ? "#2E7D32" : "#C62828" }}>
                {vsMean >= 0 ? "+" : ""}{Math.round(vsMean).toLocaleString()} vs average seat
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "0" }}>
          {smallStats.map((metric, i) => {
            const def = spec.metrics[metric];
            const value = Number(row[metric]);
            const natValue = clientData.national ? Number(clientData.national[metric]) : null;
            const diff = (!national && Number.isFinite(natValue)) ? value - natValue : null;
            return (
              <React.Fragment key={metric}>
                {i > 0 && <Box sx={{ width: "1px", bgcolor: BORDER, mx: "8px" }} />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: INK, lineHeight: 1.1 }}>
                    {formatClientMetric(value, def.format)}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: INK2, lineHeight: 1.3, mt: "1px" }}>{def.label}</Typography>
                  {diff != null && (
                    <Typography sx={{ fontSize: 9.5, color: INK3, mt: "1px" }}>
                      {diff >= 0 ? "+" : ""}{diff.toFixed(1)} vs nat
                    </Typography>
                  )}
                </Box>
              </React.Fragment>
            );
          })}
        </Box>

        {bands.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 10, color: INK2, mb: "4px", fontWeight: 600 }}>
              Customer income mix
            </Typography>
            <Box sx={{ display: "flex", height: 15, borderRadius: "3px", overflow: "hidden" }}>
              {bands.map((b) => (
                <Box key={b} sx={{
                  width: `${(Number(row[b]) / bandTotal) * 100}%`,
                  bgcolor: spec.metrics[b].band,
                }} />
              ))}
            </Box>
            {/* One line, no wrapping: five bands share the panel width, so the
                labels are the registry's short forms and the row may not fold. */}
            <Box sx={{
              display: "flex", flexWrap: "nowrap", justifyContent: "space-between",
              gap: "4px", mt: "4px", overflow: "hidden",
            }}>
              {bands.map((b) => (
                <Box key={b} sx={{ display: "flex", alignItems: "center", gap: "2.5px", minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: spec.metrics[b].band, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 9.5, color: INK2, whiteSpace: "nowrap" }}>
                    {spec.metrics[b].short || spec.metrics[b].label} {Number(row[b]).toFixed(0)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Sits on one line: the full extract description plus an ISO date ran
            past the panel edge. */}
        <Typography sx={{
          fontSize: 9, color: INK3, mt: "auto", pt: "6px",
          lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          Motability Operations
          {clientData.manifest?.ingested_at && ` · loaded ${formatLoadedDate(clientData.manifest.ingested_at)}`}
          {areasCovered < (coverage?.areas_expected || 632) && ` · ${areasCovered} seats`}
        </Typography>
      </Box>
    </SectionBox>
  );
};

/* ─── Main component ─────────────────────────────────────────────────────── */
const A4_INNER_H = 1148;

/**
 * Bounds on the fit-to-A4 scale (see the auto-scale effect). The upper bound is
 * generous because the page fills the sheet only if it's allowed to grow; the
 * lower bound stops a data-heavy page shrinking into illegibility.
 */
const MAX_UPSCALE = 1.6;
const MIN_SCALE = 0.7;

/**
 * What the fit aims for. A few px under the page so the page's own 1px border
 * and any sub-pixel rounding can't be clipped by the overflow guard.
 */
const FIT_TARGET_H = A4_INNER_H - 12;

/** Below this the page is still loading, so don't try to fill with it. */
const MIN_H_TO_UPSCALE = 700;

const EMPTY_CHARTS = {
  vi: [], awareness: [], view: [], trustGrid: [], tradeoffAccess: [], messageGrid: [],
  wave: null, prevWave: null, loading: true,
};

const MotabilityReportView = ({ constName, areaCode, national = false }) => {
  const displayName = national ? "Great Britain" : constName;
  const pageRef = useRef(null);
  const [charts, setCharts] = useState(EMPTY_CHARTS);
  const [mpData, setMpData] = useState(null);
  const [clientData, setClientData] = useState(null);

  useEffect(() => {
    if (!areaCode) return;
    let active = true;
    (async () => {
      try {
        const [vi, awareness, view, trustGrid, tradeoffAccess, messageGrid] =
          await Promise.all([
            loadGroupWaves({ groupId: "vote_intention",       areaCode }),
            loadGroupWaves({ groupId: "motability_awareness", areaCode }),
            loadGroupWaves({ groupId: "motability_view",      areaCode }),
            loadGroupWaves({ groupId: "trust_motability",     areaCode }),
            loadGroupWaves({ groupId: "tradeoff_access",      areaCode }),
            loadGroupWaves({ groupId: "motability_message",   areaCode }),
          ]);
        if (!active) return;
        // National scope: the whole-GB value lives in each row's `.national`
        // field, so lift it (and its previous-wave counterpart) into the
        // `.constituency` slots the charts read.
        const remap = (result) => {
          const rows = result?.rows || [];
          const scoped = national
            ? rows.map((r) => ({
                ...r,
                constituency: r.national,
                prevConstituency: r.prevNational,
                delta: r.deltaNational,
              }))
            : rows;
          if (SHOW_WAVE_ON_WAVE) return scoped;
          // Snapshot mode: drop every previous-wave value so no marker can
          // derive from it.
          return scoped.map((r) => ({
            ...r,
            prevConstituency: null, prevNational: null,
            delta: null, deltaNational: null,
          }));
        };
        setCharts({
          vi:             remap(vi),
          awareness:      remap(awareness),
          view:           remap(view),
          trustGrid:      remap(trustGrid),
          tradeoffAccess: remap(tradeoffAccess),
          messageGrid:    remap(messageGrid),
          // The Motability questions all ride the same waves, so the awareness
          // group (asked every wave) sets the report's fieldwork stamp.
          wave:     awareness?.wave ?? null,
          prevWave: SHOW_WAVE_ON_WAVE ? (awareness?.prevWave ?? null) : null,
          loading: false,
        });
      } catch (e) {
        console.error("[MotabilityReport]", e);
        if (!active) return;
        setCharts((prev) => ({ ...prev, loading: false }));
      }
    })();
    return () => { active = false; };
  }, [areaCode, national]);

  useEffect(() => {
    if (national || !constName) return;
    loadFullMpData(constName).then(setMpData);
  }, [constName, national]);

  useEffect(() => {
    let active = true;
    fetchClientDataset({ datasetId: "motability_customers", areaCode })
      .then((d) => { if (active) setClientData(d); });
    return () => { active = false; };
  }, [areaCode]);

  /* ── A4 auto-scale ───────────────────────────────────────────────────────
   * Fit the page to A4 in both directions: shrink when the content overflows,
   * and enlarge when it falls short so a page that would otherwise end
   * two-thirds down fills the sheet — everything grows together, so type,
   * bars and spacing keep their relative weight.
   *
   * The upscale is capped: mid-load the page is genuinely short, and without a
   * ceiling it would blow up and then snap back as data arrives.
   */
  useLayoutEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    el.style.transformOrigin = "top left";
    el.style.transform = "none";
    el.style.width = "100%";

    const natural = el.scrollHeight;
    if (!natural) return;
    // Mid-load the page is legitimately short — don't stretch a skeleton.
    if (natural < MIN_H_TO_UPSCALE && natural < A4_INNER_H) return;

    // A candidate scale is applied by laying the page out in a narrower box and
    // scaling it back up, so the height depends on the scale: narrower means
    // more wrapping means taller. Solving that with one measurement is
    // circular — measure each candidate and bisect for the largest scale that
    // still fits, so the result can only ever be at or under the page height.
    const visualHeightAt = (s) => {
      el.style.width = `${100 / s}%`;
      return el.scrollHeight * s;
    };

    // National reports carry less content than a seat report, so allowing the
    // upscale to fill the sheet blows the text up. Cap national at 1.0 — render
    // at natural size (with whitespace) rather than magnified; still downscale
    // if it ever runs long.
    const upperScale = national ? 1 : MAX_UPSCALE;
    let scale = MIN_SCALE;
    if (visualHeightAt(MIN_SCALE) <= FIT_TARGET_H) {
      let lo = MIN_SCALE;
      let hi = upperScale;
      for (let i = 0; i < 7; i++) {
        const mid = (lo + hi) / 2;
        if (visualHeightAt(mid) <= FIT_TARGET_H) lo = mid; else hi = mid;
      }
      scale = lo;
    }

    el.style.width = `${100 / scale}%`;
    el.style.transform = `scale(${scale})`;
  });

  /* ── Derived VI data ───────────────────────────────────────────────────── */
  const viData = charts.vi || [];

  const topVI = useMemo(() => {
    const top = [...viData]
      .filter((d) => Number.isFinite(Number(d?.constituency)) && d.key !== "pol:vi:dontknow:cat")
      .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0];
    return top ? { label: VI_LABELS[top.key] || top.key, constituency: top.constituency } : null;
  }, [viData]);


  /* ── Net scores with wave-on-wave change ───────────────────────────────── */
  const trustNetData = useMemo(() => buildNetRows({
    parentId: "trust_motability", rows: charts.trustGrid,
    prevWave: charts.prevWave,
  }), [charts.trustGrid, charts.prevWave]);

  const messageNetData = useMemo(() => buildNetRows({
    parentId: "motability_message", rows: charts.messageGrid,
    prevWave: charts.prevWave,
  }), [charts.messageGrid, charts.prevWave]);

  return (
    <Box sx={{ maxHeight: A4_INNER_H, overflow: "hidden" }}>
    <Box ref={pageRef} sx={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", fontSize: 11, color: INK, lineHeight: 1.5, backgroundColor: "white", border: "1px solid #ccc", p: "14px 20px" }}>

      {/* Header */}
      <MotabilityHeader
        constName={displayName}
        areaCode={areaCode}
        topVI={topVI}
        wave={charts.wave}
        prevWave={charts.prevWave}
        showNewKey={trustNetData.some((r) => r.isNew)}
        national={national}
      />

      {/* Row 1: MP Card — full width (constituency only) */}
      {!national && (
        <Box sx={{ mb: "12px" }}>
          <MPCard mpData={mpData} />
        </Box>
      )}

      {/* Row 2: the written summary, full width. Running it across the sheet
          rather than in a half-width column costs no height — the prose simply
          wraps less — and leaves both slots of the row below free. */}
      <Box sx={{ display: "flex", mb: "10px" }}>
        <MoSummaryBox
          constName={displayName}
          national={national}
          viewData={charts.view}
          awarenessData={charts.awareness}
          trustNetData={trustNetData}
          messageNetData={messageNetData}
          clientData={clientData}
          tradeoffAccess={charts.tradeoffAccess}
        />
      </Box>

      {/* Row 3: who the customers are | what the country makes of the Scheme.
          The client's own data leads the page: it's the one panel here that
          isn't survey output, and it frames every figure below it. */}
      <Box sx={{ display: "flex", gap: "10px", mb: "10px", alignItems: "stretch" }}>
        <ClientCustomersPanel clientData={clientData} national={national} />
        <SectionBox title="General View" accent="#2E7D32" flex={1.1} minHeight={165}>
          <GeneralViewPanel
            view={charts.view}
            wave={charts.wave}
            prevWave={charts.prevWave}
            hideNational={national}
          />
        </SectionBox>
      </Box>

      {/* Row 4: Awareness | Trust */}
      <Box sx={{ display: "flex", gap: "10px", mb: "10px", alignItems: "stretch" }}>
        {/* Trust carries four figures per aspect and awareness only one per
            answer, so the width goes where the columns are. */}
        <SectionBox title="Awareness" accent="#0097A7" flex={1} minHeight={165}>
          <AwarenessChart data={charts.awareness} hideNational={national} />
        </SectionBox>
        <SectionBox title="Trust in the Scheme" accent="#1565C0" flex={1.85} minHeight={165}>
          <TrustComposition
            rows={trustNetData}
            caption={'"To what degree would you trust the Motability Scheme to…?"'}
            hideNational={national}
          />
        </SectionBox>
      </Box>

      {/* Row 5: Messages | Access tradeoff. The tradeoff is a two-figure stat,
          so it rides alongside the messages rather than taking a row. */}
      <Box sx={{ display: "flex", gap: "10px", alignItems: "stretch" }}>
        <SectionBox title="Message Testing" accent="#B45309" flex={2.4} minHeight={210}>
          <NetScoreRows
            rows={messageNetData}
            caption="Net agreement with each message statement"
            leftLabel="Disagree" rightLabel="Agree"
            defaultColor="#B45309"
            hideNational={national}
            barHeight={14}
          />
        </SectionBox>

        <TradeoffStat
          title="Tradeoff: Access"
          data={charts.tradeoffAccess}
          accent="#F57C00"
          hideNational={national}
        />
      </Box>

    </Box>
    </Box>
  );
};

export default MotabilityReportView;
